import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Menu, ArrowLeft } from "lucide-react";
import { landingContent } from "../data/landingContent";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetDescription,
} from "../components/ui/sheet";

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#f7f7f8]">
      <SiteHeader />
      <main>
        <section className="relative overflow-hidden py-14 md:py-18">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_10%_15%,rgba(13,148,136,0.10),transparent_55%),radial-gradient(circle_at_70%_25%,rgba(245,158,11,0.10),transparent_55%)]" />
            <div className="werelay-noise absolute inset-0 opacity-[0.12]" />
          </div>

          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <div className="mb-6">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Link>
            </div>

            <div className="werelay-glass werelay-card rounded-3xl border border-white/40 p-8 md:p-10 shadow-[0_25px_80px_rgba(2,6,23,0.10)] relative overflow-hidden">
              <div className="relative">
                <div className="mb-8">
                  <h1 className="text-4xl md:text-5xl font-semibold tracking-tight text-[#0f766e] mb-4">
                    Privacy Policy
                  </h1>
                  <p className="text-lg text-muted-foreground">
                    How we collect, use, and protect your information
                  </p>
                  <p className="mt-4 text-sm text-muted-foreground">
                    Effective Date: <strong>May 1, 2025</strong>
                  </p>
                </div>

                <div className="prose prose-sm max-w-none">
                  <div className="space-y-6 text-muted-foreground">
                  <p>
                    This Privacy Policy applies to <strong>WeRelay</strong>, an
                    application developed and operated by{" "}
                    <strong>WeIncept Solutions Private Limited</strong> (
                    "WeIncept", "Company", "we", "our", or "us").
                  </p>

                  <p>
                    WeRelay provides infrastructure for sending and managing
                    messages over WhatsApp. This policy explains how we collect,
                    use, disclose, and protect user information when you use
                    WeRelay.
                  </p>

                  <section className="mt-8">
                    <h2 className="text-2xl font-semibold text-[#0f766e] mb-4">
                      1. About WeRelay
                    </h2>
                    <p>
                      WeRelay is a communication-enablement platform that
                      allows businesses to send and manage messages over
                      WhatsApp using APIs and dashboards. It integrates with
                      WhatsApp Business and does not operate as a standalone
                      messaging service.
                    </p>
                  </section>

                  <section className="mt-8">
                    <h2 className="text-2xl font-semibold text-[#0f766e] mb-4">
                      2. Information We Collect
                    </h2>

                    <h3 className="text-lg font-semibold text-[#0f766e] mt-6 mb-3">
                      a) Business Information:
                    </h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Business name, contact details</li>
                      <li>
                        WhatsApp Business Account credentials and settings
                      </li>
                      <li>
                        Sender and receiver details (phone numbers, contact
                        names)
                      </li>
                    </ul>

                    <h3 className="text-lg font-semibold text-[#0f766e] mt-6 mb-3">
                      b) Message Metadata:
                    </h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Timestamps of messages sent and delivered</li>
                      <li>Message status (delivered, failed, read)</li>
                      <li>Recipient phone numbers</li>
                      <li>Templates used, message types (text/media)</li>
                    </ul>
                    <p className="mt-3">
                      WeRelay does not access or store message content unless
                      explicitly configured for logging or reporting by the
                      business user.
                    </p>

                    <h3 className="text-lg font-semibold text-[#0f766e] mt-6 mb-3">
                      c) Technical Information:
                    </h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>API usage data</li>
                      <li>
                        Device/browser information when using the dashboard
                      </li>
                      <li>IP addresses, login time, access logs</li>
                    </ul>
                  </section>

                  <section className="mt-8">
                    <h2 className="text-2xl font-semibold text-[#0f766e] mb-4">
                      3. How We Use Your Information
                    </h2>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        Facilitate message transmission via WhatsApp Business
                        APIs
                      </li>
                      <li>
                        Provide delivery tracking, reporting, and analytics
                      </li>
                      <li>
                        Troubleshoot issues and ensure platform reliability
                      </li>
                      <li>
                        Prevent fraud, abuse, or unauthorized access
                      </li>
                      <li>
                        Communicate about account activity, updates, or service
                        changes
                      </li>
                    </ul>
                  </section>

                  <section className="mt-8">
                    <h2 className="text-2xl font-semibold text-[#0f766e] mb-4">
                      4. Data Sharing and Disclosure
                    </h2>
                    <p>
                      We do not sell your data. We may share it with:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-3">
                      <li>WhatsApp (Meta) for message delivery</li>
                      <li>
                        Authorized service providers (e.g., cloud, analytics)
                      </li>
                      <li>
                        Law enforcement or regulators where legally required
                      </li>
                      <li>
                        Internal employees or contractors under confidentiality
                      </li>
                    </ul>
                  </section>

                  <section className="mt-8">
                    <h2 className="text-2xl font-semibold text-[#0f766e] mb-4">
                      5. Data Storage and Security
                    </h2>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Data hosted on secure cloud servers in India</li>
                      <li>
                        Encryption, access control, and secure API practices
                      </li>
                      <li>Role-based access for sensitive information</li>
                    </ul>
                  </section>

                  <section className="mt-8">
                    <h2 className="text-2xl font-semibold text-[#0f766e] mb-4">
                      6. Retention of Data
                    </h2>
                    <p>
                      Message metadata is retained during the client
                      relationship and up to <strong>6 months</strong>{" "}
                      thereafter, unless required otherwise by law.
                    </p>
                    <p className="mt-3">
                      Businesses may configure custom retention policies via the
                      WeRelay platform.
                    </p>
                  </section>

                  <section className="mt-8">
                    <h2 className="text-2xl font-semibold text-[#0f766e] mb-4">
                      7. Your Rights
                    </h2>
                    <p>You may:</p>
                    <ul className="list-disc pl-6 space-y-2 mt-3">
                      <li>
                        Access and review metadata stored by WeRelay
                      </li>
                      <li>
                        Request correction or deletion of inaccurate data
                      </li>
                      <li>
                        Revoke access to your WhatsApp Business credentials
                        (this may affect services)
                      </li>
                    </ul>
                    <p className="mt-3">
                      To exercise these rights, contact us at{" "}
                      <a
                        href="mailto:privacy@weincept.com"
                        className="text-[#0f766e] hover:underline"
                      >
                        privacy@weincept.com
                      </a>
                      .
                    </p>
                  </section>

                  <section className="mt-8">
                    <h2 className="text-2xl font-semibold text-[#0f766e] mb-4">
                      8. Compliance with WhatsApp Policies
                    </h2>
                    <p>
                      WeRelay integrates only with WhatsApp Business API via
                      official providers. Clients must follow WhatsApp's
                      Business Policy and Messaging Guidelines. WeIncept is
                      not liable for violations by users.
                    </p>
                  </section>

                  <section className="mt-8">
                    <h2 className="text-2xl font-semibold text-[#0f766e] mb-4">
                      9. Children's Privacy
                    </h2>
                    <p>
                      WeRelay is not intended for individuals under 18. We do
                      not knowingly collect data from minors.
                    </p>
                  </section>

                  <section className="mt-8">
                    <h2 className="text-2xl font-semibold text-[#0f766e] mb-4">
                      10. Changes to This Policy
                    </h2>
                    <p>
                      We may update this Privacy Policy. Updates will be posted
                      on our website and/or the WeRelay dashboard with a
                      revised "Effective Date."
                    </p>
                  </section>

                  <section className="mt-8">
                    <h2 className="text-2xl font-semibold text-[#0f766e] mb-4">
                      11. Contact Us
                    </h2>
                    <div className="space-y-2">
                      <p className="font-semibold text-foreground">
                        WeIncept Solutions Private Limited
                      </p>
                      <p>
                        Ground Floor D-9, Block D, Noida Sec 3, Noida, Uttar
                        Pradesh 201301
                      </p>
                      <p>
                        Email:{" "}
                        <a
                          href="mailto:privacy@weincept.com"
                          className="text-[#0f766e] hover:underline"
                        >
                          privacy@weincept.com
                        </a>
                      </p>
                      <p>Phone: +91-8595714205</p>
                      <p>
                        Website:{" "}
                        <a
                          href="https://www.weincept.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#0f766e] hover:underline"
                        >
                          www.weincept.com
                        </a>
                      </p>
                    </div>
                  </section>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={
        "sticky top-0 z-50 border-b transition-colors duration-300 " +
        (scrolled
          ? "border-black/10 bg-white/65 backdrop-blur-[18px]"
          : "border-transparent bg-transparent")
      }
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2" aria-label="Home">
            <img
              src={landingContent.brand.logoMark}
              alt="WeRelay"
              className="h-7 w-auto"
              loading="eager"
            />
            <span className="text-lg font-semibold text-foreground">
              WeRelay App
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <HeaderLink href="/#why" label="Why" />
            <HeaderLink href="/#capabilities" label="Capabilities" />
            <HeaderLink href="/#how" label="How" />
            <HeaderLink href="/#api" label="API" />
            <HeaderLink href="/#reliability" label="Reliability" />
            <HeaderLink href="/#faq" label="FAQ" />
          </nav>

          <div className="flex items-center gap-2">
            {/* Mobile menu */}
            <div className="md:hidden">
              <Sheet>
                <SheetTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-10 rounded-xl bg-white/60 border-black/10 hover:bg-white"
                    aria-label="Open menu"
                  >
                    <Menu className="h-4 w-4" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="p-0">
                  <SheetHeader className="px-6 pt-6 pb-4">
                    <SheetTitle className="flex items-center gap-2">
                      <img
                        src={landingContent.brand.logoMark}
                        alt="WeRelay"
                        className="h-7 w-auto"
                        loading="lazy"
                      />
                      <span className="text-lg font-semibold text-foreground">
                        WeRelay App
                      </span>
                    </SheetTitle>
                    <SheetDescription className="sr-only">
                      Navigation menu
                    </SheetDescription>
                  </SheetHeader>

                  <div className="px-6 pb-6">
                    <div className="grid gap-1">
                      <MobileNavLink href="/#why" label="Why" />
                      <MobileNavLink href="/#capabilities" label="Capabilities" />
                      <MobileNavLink href="/#how" label="How" />
                      <MobileNavLink href="/#api" label="API" />
                      <MobileNavLink href="/#reliability" label="Reliability" />
                      <MobileNavLink href="/#faq" label="FAQ" />
                    </div>

                    <div className="mt-6 grid gap-2">
                      <Button
                        asChild
                        variant="outline"
                        className="h-11 rounded-xl bg-white/70 border-black/10 hover:bg-white"
                      >
                        <Link to="/">Home</Link>
                      </Button>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            <Button
              asChild
              variant="outline"
              className="hidden sm:inline-flex h-10 rounded-xl bg-white/60 border-black/10 hover:bg-white"
            >
              <Link to="/">Home</Link>
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}

function HeaderLink({ href, label }) {
  return (
    <a
      href={href}
      data-header-nav="true"
      className="rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground"
    >
      {label}
    </a>
  );
}

function SiteFooter() {
  return (
    <footer className="border-t border-black/10 bg-[#f7f7f8]">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="text-lg font-semibold text-foreground">
              {landingContent.brand.name}
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              {landingContent.footer.tagline}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-6 sm:grid-cols-2">
            <FooterCol
              title="Product"
              links={landingContent.footer.productLinks.map((link) => ({
                label: link.label,
                href: landingContent.links[link.href] || `#${link.href}`,
              }))}
            />
            <FooterCol
              title="Company"
              links={landingContent.footer.companyLinks.map((link) => ({
                label: link.label,
                href: landingContent.links[link.href] || `#${link.href}`,
              }))}
            />
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-3 border-t border-black/10 pt-6 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <img
                className="h-5 w-5 rounded"
                src={landingContent.brand.logoMark}
                alt="WeRelay"
              />
              <span className="text-sm text-foreground font-medium">
                WeRelay
              </span>
              <span className="text-xs text-muted-foreground">
                by WeIncept
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Phone className="w-3 h-3 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">
                +91-8595714205
              </span>
            </div>
            <span className="text-xs text-muted-foreground">
              Built with ❤️ in India
            </span>
          </div>
          <div className="text-xs text-muted-foreground">
            {landingContent.footer.tagline2}
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <div className="text-sm font-semibold text-foreground">{title}</div>
      <ul className="mt-3 space-y-2">
        {links.map((l) => (
          <li key={l.label}>
            {l.href.startsWith("/") ? (
              <Link
                to={l.href}
                className="text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground"
              >
                {l.label}
              </Link>
            ) : (
              <a
                href={l.href}
                className="text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground"
              >
                {l.label}
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function MobileNavLink({ href, label }) {
  return (
    <SheetClose asChild>
      <a
        href={href}
        data-mobile-nav="true"
        className="rounded-xl px-3 py-3 text-base text-foreground/90 transition-colors duration-200 hover:bg-black/5"
      >
        {label}
      </a>
    </SheetClose>
  );
}

export default PrivacyPolicy;

